export const EMPHASIS_RULE_LIMIT = 200;
export const EMPHASIS_TERM_LIMIT = 200;

export type EmphasisStyle = "bold" | "italic" | "boldItalic";

export type EmphasisRules = Record<EmphasisStyle, string[]>;

export type EmphasisSegment = {
  text: string;
  bold: boolean;
  italic: boolean;
  style: EmphasisStyle | null;
};

export type EmphasisTermsInspection = {
  terms: string[];
  valid: boolean;
  tooLongCount: number;
  tooMany: boolean;
};

type EmphasisMatch = {
  start: number;
  end: number;
  style: EmphasisStyle;
  priority: number;
  bold: boolean;
  italic: boolean;
  order: number;
};

const STYLE_DEFINITIONS = [
  {
    style: "boldItalic",
    priority: 3,
    bold: true,
    italic: true,
  },
  { style: "bold", priority: 2, bold: true, italic: false },
  { style: "italic", priority: 1, bold: false, italic: true },
] as const;

export const emptyEmphasisRules = (): EmphasisRules => ({
  bold: [],
  italic: [],
  boldItalic: [],
});

export function normalizeEmphasisTerms(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const item of value) {
    if (typeof item !== "string") continue;
    for (const line of item.split(/\r?\n/)) {
      const term = line.trim().slice(0, EMPHASIS_TERM_LIMIT);
      if (!term || seen.has(term)) continue;
      seen.add(term);
      terms.push(term);
      if (terms.length === EMPHASIS_RULE_LIMIT) return terms;
    }
  }
  return terms;
}

export function normalizeEmphasisRules(value: unknown): EmphasisRules {
  const candidate =
    value && typeof value === "object"
      ? (value as Partial<Record<EmphasisStyle, unknown>>)
      : {};
  return {
    bold: normalizeEmphasisTerms(candidate.bold),
    italic: normalizeEmphasisTerms(candidate.italic),
    boldItalic: normalizeEmphasisTerms(candidate.boldItalic),
  };
}

export function parseEmphasisTermsText(value: string): string[] {
  return normalizeEmphasisTerms(value.split(/\r?\n/));
}

export function inspectEmphasisTermsText(
  value: string,
): EmphasisTermsInspection {
  const seen = new Set<string>();
  const terms: string[] = [];
  let tooLongCount = 0;
  for (const line of value.split(/\r?\n/)) {
    const term = line.trim();
    if (!term || seen.has(term)) continue;
    seen.add(term);
    if (term.length > EMPHASIS_TERM_LIMIT) {
      tooLongCount += 1;
      continue;
    }
    terms.push(term);
  }
  const tooMany = terms.length > EMPHASIS_RULE_LIMIT;
  return {
    terms: terms.slice(0, EMPHASIS_RULE_LIMIT),
    valid: tooLongCount === 0 && !tooMany,
    tooLongCount,
    tooMany,
  };
}

export function findEmphasisMatches(
  text: string,
  rules: EmphasisRules,
): EmphasisMatch[] {
  if (!text) return [];
  const candidates: EmphasisMatch[] = [];
  let order = 0;

  for (const definition of STYLE_DEFINITIONS) {
    for (const term of rules[definition.style]) {
      if (!term) continue;
      let cursor = 0;
      while (cursor <= text.length - term.length) {
        const start = text.indexOf(term, cursor);
        if (start < 0) break;
        candidates.push({
          start,
          end: start + term.length,
          style: definition.style,
          priority: definition.priority,
          bold: definition.bold,
          italic: definition.italic,
          order: order++,
        });
        cursor = start + 1;
      }
    }
  }

  candidates.sort(
    (left, right) =>
      left.start - right.start ||
      right.priority - left.priority ||
      right.end - right.start - (left.end - left.start) ||
      left.order - right.order,
  );

  const occupied = Array.from({ length: text.length }, () => false);
  const accepted: EmphasisMatch[] = [];
  for (const candidate of candidates) {
    let overlaps = false;
    for (let index = candidate.start; index < candidate.end; index += 1) {
      if (occupied[index]) {
        overlaps = true;
        break;
      }
    }
    if (overlaps) continue;
    for (let index = candidate.start; index < candidate.end; index += 1) {
      occupied[index] = true;
    }
    accepted.push(candidate);
  }

  return accepted.sort((left, right) => left.start - right.start);
}

export function segmentTextByEmphasis(
  text: string,
  inputRules: EmphasisRules,
): EmphasisSegment[] {
  if (!text) return [];
  const rules = normalizeEmphasisRules(inputRules);
  const matches = findEmphasisMatches(text, rules);
  if (!matches.length) {
    return [{ text, bold: false, italic: false, style: null }];
  }

  const segments: EmphasisSegment[] = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start > cursor) {
      segments.push({
        text: text.slice(cursor, match.start),
        bold: false,
        italic: false,
        style: null,
      });
    }
    segments.push({
      text: text.slice(match.start, match.end),
      bold: match.bold,
      italic: match.italic,
      style: match.style,
    });
    cursor = match.end;
  }
  if (cursor < text.length) {
    segments.push({
      text: text.slice(cursor),
      bold: false,
      italic: false,
      style: null,
    });
  }
  return segments;
}

export function findCrossStyleConflicts(rules: EmphasisRules): string[] {
  const memberships = new Map<string, number>();
  for (const definition of STYLE_DEFINITIONS) {
    for (const term of rules[definition.style]) {
      memberships.set(term, (memberships.get(term) ?? 0) + 1);
    }
  }
  return [...memberships.entries()]
    .filter(([, count]) => count > 1)
    .map(([term]) => term);
}
